import type { ConnectionSchemas } from "api-types";
import { get } from "lodash";
import type { IntlShape } from "react-intl";

import type { CompleteConnectionModalProps } from "../../components/complete-connection-modal/complete-connection-modal";
import { cancelConnection, completeConnection } from "../services";
import { dataSourceSlugToName } from "../utils";

/**
 * Entry point to the transformation
 */
export const completeConnectionTransformer = (
  intl: IntlShape,
  props: ConnectionSchemas.CompleteConnectionPropsSchema | undefined,
  context: any,
  toaster: any
): CompleteConnectionModalProps | null => {
  return props && !props.oAuthError
    ? dataSourceTransformers[props.dataSourceSlug](intl, props, context, toaster)
    : null;
};

const commonProps = (intl, props, context, toaster) => ({
  header: intl.formatMessage({
    id: "shared-ui: Confirm account to connect",
    defaultMessage: "Confirm account to connect",
  }),
  open: true,
  connectClickHandler: (data) => completeConnection(props, context, data, toaster),
  cancelClickHandler: () => cancelConnection(props, context, toaster),
  confirmDisabled: () => false,
});

/**
 * Handles the complete connection modal variant that renders a simple confirmation and references information from the `store` object
 *
 * ebay = store.name
 * stripe = store.name
 * woocommerce = store.domain (store.currency)
 * magento = store.name (store.currency)
 */
const storeTransformer = (
  intl: IntlShape,
  props: ConnectionSchemas.CompleteConnectionPropsSchema,
  context: any,
  toaster: { error: CallableFunction }
): CompleteConnectionModalProps | null => {
  if (props["store"]) {
    return {
      body: intl.formatMessage(
        {
          id: `shared-ui: account selection hint`,
          defaultMessage:
            "Would you like us to start downloading and analysing your data from this {dataSource} account?",
        },
        { dataSource: dataSourceSlugToName(props.dataSourceSlug) }
      ),
      info: storeInfo[props.dataSourceSlug] ? storeInfo[props.dataSourceSlug](props.store) : get(props, "store.name"),
      ...commonProps(intl, props, context, toaster),
    };
  } else if (props.firstConnection) {
    return {
      // This is never used, as it's overwritten in commonProps. TODO: Either remove or move this line below
      // header: _(`successfully granted permissions`, { dataSource: dataSourceSlugToName(props.dataSourceSlug) }),
      body: intl.formatMessage(
        {
          id: `shared-ui: confirm download data`,
          defaultMessage: "Would you like to start downloading and analysing your data from {dataSource}?",
        },
        { dataSource: dataSourceSlugToName(props.dataSourceSlug) }
      ),
      ...commonProps(intl, props, context, toaster),
    };
  }
  return null;
};

/**
 * Transforms the store object to the storeInfo string
 */
const storeInfo = {
  shopify2: (store) => `${get(store, "name")} ${get(store, "currency")} (${get(store, "domain")})`,
  magento2: (store) => `${get(store, "name")} (${get(store, "currency")})`,
  mockify: (store) => `${get(store, "name")} (${get(store, "currency")})`,
  woocommerce2: (store) => `${get(store, "domain")} (${get(store, "currency")})`,
  shopware5: (store) => `${get(store, "domain")} (${get(store, "currency")})`,
  prestashop: (store) => `${get(store, "domain")} (${get(store, "currency")})`,
};

export const confirmDisabled = (selectedAccounts) => {
  if (!selectedAccounts || !Array.isArray(selectedAccounts) || !selectedAccounts.length) {
    return false;
  }
  return (
    selectedAccounts.filter((a) => {
      if (a.properties) {
        return !!a.properties.filter((p) => p.selected).length;
      }
      return a.selected;
    }).length === 0
  );
};

/**
 * Handles the transformation for the complete connection modal that renders a list of accounts for the user to select from
 */
export const accountTransformer = (
  intl: IntlShape,
  props: ConnectionSchemas.CompleteConnectionPropsSchema,
  context: any,
  toaster: { error: CallableFunction }
): CompleteConnectionModalProps | null => {
  const requiredTransformers = [accountTransformers, connectClickHandlers, singleConfirmableAccount];
  for (let i = 0; i < requiredTransformers.length; i++) {
    const transformer = requiredTransformers[i];
    if (!transformer[props.dataSourceSlug]) {
      throw new Error(`${props.dataSourceSlug} is not registered in ${transformer}`);
    }
  }

  if ("accounts" in props && Array.isArray(props.accounts)) {
    const accounts = props.accounts.map(accountTransformers[props.dataSourceSlug]) as any[]; //@todo - better typing here requires Account exposed from flyui

    const connectClickHandler = connectClickHandlers[props.dataSourceSlug](props, context);

    if (accounts.length >= 1) {
      if (singleConfirmableAccount[props.dataSourceSlug](accounts)) {
        return {
          body: intl.formatMessage(
            {
              id: `shared-ui: account selection hint`,
              defaultMessage:
                "Would you like us to start downloading and analysing your data from this {dataSource} account?",
            },
            { dataSource: dataSourceSlugToName(props.dataSourceSlug) }
          ),
          ...commonProps(intl, props, context, toaster),
          accounts,
          connectClickHandler,
        };
      } else {
        return {
          body: intl.formatMessage({
            id: "shared-ui: select properties store hint",
            defaultMessage: "Please select properties associated with your store.",
          }),
          ...commonProps(intl, props, context, toaster),
          accounts,
          connectClickHandler,
          confirmDisabled,
        };
      }
    }
  }
  // TODO: raise an error here once error boundaries have been established
  return null;
};

const singleConfirmableAccount = {
  google_ads2: (accounts) => accounts.length === 1,
  facebook2: (accounts) => accounts.length === 1,
  tiktok: (accounts) => accounts.length === 1,
  ga: (accounts) => accounts.length === 1 && accounts[0].properties.length === 1,
};

function gaTransform() {
  return (account) => ({
    selected: true,
    ...account,
    label: account.name,
    properties: Array.isArray(account.properties)
      ? account.properties.map((property) => ({
          selected: true,
          ...property,
          label: property.name + (property.url !== undefined ? `(${property.url})` : ""),
        }))
      : [],
  });
}

/**
 * Responsible for the transformation of the accounts array sent from the server into the accounts array accepted by the onboarding interface
 * This requires a "selected" flag to be set to control the state of the checkbox and for the expected label to be a sibling of selected
 * under the key "name" (TODO: potentially rename the interface property to "label" to avoid clashes)
 */
const accountTransformers = {
  google_ads2: (account) => ({ selected: true, ...account, label: account.name }),
  facebook2: (account) => ({ selected: true, ...account, label: accountNameString(account, AccountTypeEnum.FACEBOOK) }),
  tiktok: (account) => ({ selected: true, ...account, label: accountNameString(account, AccountTypeEnum.TIKTOK) }),
  ga: gaTransform(),
};

// Types for account transformer
enum AccountTypeEnum {
  FACEBOOK = "business_name",
  TIKTOK = "company",
}

//Prepare name string for account transformer
const accountNameString = (account, type: AccountTypeEnum) =>
  `${get(account, "name")}${get(account, type, "").length > 0 ? " - " + get(account, type) : ""}`;

function gaClickConnect() {
  return (props, context) => (accounts) => {
    /**
     * A fairly ugly operation to filter for accounts with a property that contains selected=true and then create
     * an object for each view in that property that contains the account_id, property_id and view_id and flatten
     * into a single dimensional array.
     *
     * Example:
     * accounts = [{id:1,properties:[{id:1,views:[{id:1},{id:2}]}]},{id:2,properties:[{id:1,views:[{id:1},{id:2}]}]}]
     * result = [{account_id:1,property_id:1,view_id:1},{account_id:1,property_id:1,view_id:2},{account_id:2,property_id:1,view_id:1}]
     */
    completeConnection(props, context, {
      viewIdentifiers: accounts
        .filter(
          // filter for accounts with properties, that are selected
          (ac) => Array.isArray(ac.properties) && ac.properties.length && ac.selected
        )
        .map(mapPropertiesToViews)
        .flat()
        .flat(),
    });
  };
}

export const mapPropertiesToViews = (account) => {
  // filter proprties with views, that are selected
  return account.properties
    .filter((prop) => Array.isArray(prop.views) && (prop.type === "GA4" || prop.views.length) && prop.selected)
    .map((prop) =>
      prop.type === "GA4"
        ? [{ account_id: account.id, property_name: prop.id }]
        : prop.views.map((view) => ({ account_id: account.id, property_id: prop.id, view_id: view.id }))
    );
};

/**
 * The connect clickHandlers are responsible for recieving the form information from the onboarding screen
 * (create in the accountTransformers above) and transforming it into the data object that is expected by the backend
 */
const connectClickHandlers = {
  google_ads2: (props, context) => (data) => {
    completeConnection(props, context, {
      accountIdentifiers: data.filter((ac) => ac.selected).map((ac) => ({ id: ac.id })),
    });
  },
  facebook2: (props, context) => (data) => {
    completeConnection(props, context, {
      accountIdentifiers: data.filter((ac) => ac.selected).map((ac) => ({ id: ac.id })),
    });
  },
  tiktok: (props, context) => (data) => {
    completeConnection(props, context, {
      accountIdentifiers: data.filter((ac) => ac.selected).map((ac) => ({ id: ac.id })),
    });
  },
  ga: gaClickConnect(),
};

/**
 * Other variant - simple confirmation message rendered in the modal
 */
const otherTransformer = (
  intl: IntlShape,
  props: ConnectionSchemas.CompleteConnectionPropsSchema,
  context: any,
  toaster: { error: CallableFunction }
): CompleteConnectionModalProps | null => {
  if (props.firstConnection) {
    return {
      // This is never used, as it's overwritten in commonProps. TODO: Either remove or move this line below
      // header: _(`You have succesfully granted us access to your account.`, {
      //   dataSource: dataSourceSlugToName(props.dataSourceSlug),
      // }),
      body: intl.formatMessage(
        {
          id: `shared-ui: confirm download data`,
          defaultMessage: "Would you like to start downloading and analysing your data from {dataSource}?",
        },
        { dataSource: dataSourceSlugToName(props.dataSourceSlug) }
      ),
      ...commonProps(intl, props, context, toaster),
    };
  }
  return null;
};

type CompleteConnectionTransformer = (
  intl: IntlShape,
  props: ConnectionSchemas.CompleteConnectionPropsSchema,
  context: any,
  toaster: { error: CallableFunction }
) => CompleteConnectionModalProps | null;

interface CompleteConnectionTransformers {
  [key: string]: CompleteConnectionTransformer;
}

export const dataSourceTransformers: CompleteConnectionTransformers = {
  google_ads2: accountTransformer,
  ebay: storeTransformer,
  facebook2: accountTransformer,
  ga: accountTransformer,
  magento2: storeTransformer,
  mockify: storeTransformer,
  prestashop: storeTransformer,
  shopify2: storeTransformer,
  stripe2: storeTransformer,
  woocommerce2: storeTransformer,
  amazonrutter: otherTransformer,
  bigcommercerutter: storeTransformer,
  shopware5: storeTransformer,
  tiktok: accountTransformer,
  other: otherTransformer,
};
